import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { QuoteFieldErrors, ServiceKey } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { ArrowRight, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { gsap, MOTION, useGSAP } from '@/lib/motion'
import { normalizeQuote, QUOTE_LIMITS, validateQuote } from '@/lib/quote'
import { PREFILL_EVENT, takePendingPrefill } from '@/lib/quote-prefill'
import { HouseMark } from '@/components/generals/house-mark'
import { LocalTime } from '@/components/generals/local-time'
import { SplitWords } from '@/components/generals/split-words'
import { QUOTE_HASH, SERVICE_LABELS, siteConfig } from '@/constants/site'

type Status = 'idle' | 'submitting' | 'success' | 'error'

type Fields = {
    firstName: string
    lastName: string
    email: string
    message: string
    website: string
}

const EMPTY: Fields = {
    firstName: '',
    lastName: '',
    email: '',
    message: '',
    website: ''
}

const FIELD_ORDER: Array<keyof QuoteFieldErrors> = ['firstName', 'lastName', 'email', 'message']

const SERVICE_NOTES: Partial<Record<ServiceKey, string>> = {
    seo: "Currently full"
}

interface TextFieldProps {
    id: string
    name: keyof Fields
    label: string
    value: string
    error?: string
    type?: string
    autoComplete?: string
    onChange: (event: ChangeEvent<HTMLInputElement>) => void
}

const TextField: FC<TextFieldProps> = ({ id, name, label, value, error, type = 'text', autoComplete, onChange }): ReactNode => {
    return (
        <div className='flex flex-col'>
            <label htmlFor={id} className='label text-stone'>
                {label}
                <span aria-hidden='true' className='text-signal-deep'> *</span>
            </label>

            <input
                id={id}
                name={name}
                type={type}
                value={value}
                required
                autoComplete={autoComplete}
                maxLength={name === 'email' ? QUOTE_LIMITS.email : QUOTE_LIMITS.name}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? `${id}-error` : undefined}
                onChange={onChange}
                className={cn(
                    'mt-2 h-14 w-full border-b-2 bg-transparent text-xl text-ink outline-none transition-colors duration-300 placeholder:text-stone/60 focus:border-signal md:text-2xl',
                    error ? 'border-destructive' : 'border-ink/20 hover:border-ink/50'
                )}
            />

            {error && (
                <p id={`${id}-error`} className='mt-2 text-sm font-medium text-destructive'>
                    {error}
                </p>
            )}
        </div>
    )
}

/** Chapter ten: the free quote request, saved for the owner and emailed straight to their inbox. */
export const QuoteSection: FC = (): ReactNode => {
    const ref = useRef<HTMLElement>(null)
    const formRef = useRef<HTMLFormElement>(null)
    const successRef = useRef<HTMLDivElement>(null)
    const [fields, setFields] = useState<Fields>(EMPTY)
    const [services, setServices] = useState<Array<ServiceKey>>([])
    const [errors, setErrors] = useState<QuoteFieldErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [notice, setNotice] = useState('')
    const [sentTo, setSentTo] = useState('')

    useEffect(() => {
        const add = (service: ServiceKey) => setServices(current => (current.includes(service) ? current : [...current, service]))
        const pending = takePendingPrefill()

        if (pending) add(pending)

        const onPrefill = (event: Event) => {
            const service = (event as CustomEvent<ServiceKey>).detail

            takePendingPrefill()
            if (service) add(service)
        }

        window.addEventListener(PREFILL_EVENT, onPrefill)

        return () => window.removeEventListener(PREFILL_EVENT, onPrefill)
    }, [])

    useEffect(() => {
        if (status === 'success') successRef.current?.focus({ preventScroll: true })
    }, [status])

    useGSAP(() => {
        const section = ref.current

        if (!section) return

        const q = gsap.utils.selector(section)
        const mm = gsap.matchMedia()

        mm.add(MOTION.ok, () => {
            gsap.fromTo(q('[data-title] [data-word]'), { yPercent: 110 }, {
                yPercent: 0,
                duration: 1.2,
                stagger: 0.06,
                ease: 'expo.out',
                scrollTrigger: { trigger: q('[data-title]')[0], start: 'top 82%', toggleActions: 'play none none reverse' }
            })

            gsap.fromTo(q('[data-field]'), { autoAlpha: 0, y: 30 }, {
                autoAlpha: 1,
                y: 0,
                duration: 0.9,
                stagger: 0.08,
                ease: 'expo.out',
                scrollTrigger: { trigger: formRef.current, start: 'top 85%', toggleActions: 'play none none none' }
            })
        })

        return () => mm.revert()
    }, { scope: ref })

    const update = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const name = event.target.name as keyof Fields

        setFields(current => ({ ...current, [name]: event.target.value }))

        if (errors[name as keyof QuoteFieldErrors]) {
            setErrors(current => ({ ...current, [name]: undefined }))
        }
    }

    const toggleService = (service: ServiceKey) => {
        setServices(current => (current.includes(service) ? current.filter(item => item !== service) : [...current, service]))
    }

    const focusFirstError = (found: QuoteFieldErrors) => {
        const first = FIELD_ORDER.find(key => found[key])

        if (first) formRef.current?.querySelector<HTMLElement>(`[name="${first}"]`)?.focus()
    }

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'submitting') return

        const input = normalizeQuote({ ...fields, services })
        const found = validateQuote(input)

        setErrors(found)
        setNotice('')

        if (Object.keys(found).length > 0) {
            focusFirstError(found)

            return
        }

        setStatus('submitting')

        try {
            const response = await fetch('/api/quote', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(input)
            })

            const data = (await response.json().catch(() => null)) as { ok?: boolean, message?: string, errors?: QuoteFieldErrors } | null

            if (!response.ok || !data?.ok) {
                if (data?.errors) {
                    setErrors(data.errors)
                    focusFirstError(data.errors)
                }

                setNotice(data?.message ?? "Something went wrong on our side. Please try again.")
                setStatus('error')

                return
            }

            setSentTo(input.firstName)
            setFields(EMPTY)
            setServices([])
            setStatus('success')
        }
        catch {
            setNotice("We couldn't reach our server. Please check your connection and try again.")
            setStatus('error')
        }
    }

    const reset = () => {
        setStatus('idle')
        setNotice('')
        window.requestAnimationFrame(() => formRef.current?.querySelector<HTMLElement>('input[name="firstName"]')?.focus())
    }

    const submitting = status === 'submitting'

    return (
        <section
            ref={ref}
            id={QUOTE_HASH}
            data-tone='paper'
            data-surface='light'
            aria-labelledby='quote-title'
            className='relative bg-paper px-(--gutter) pb-[16vh] pt-[18vh] text-ink outline-none'
        >
            <div className='grid gap-x-10 gap-y-16 lg:grid-cols-12'>
                <div className='lg:col-span-5'>
                    <p className='label text-stone'>
                        <span className='mr-3'>07</span>
                        Free quote
                    </p>

                    <h2
                        id='quote-title'
                        data-title=''
                        className='display mt-6 text-[clamp(3rem,6.8vw,8rem)] leading-[0.86] wdth-75'
                    >
                        <SplitWords text="Request a free quote" />
                    </h2>

                    <p className='mt-8 max-w-[36ch] text-lg leading-relaxed text-stone md:text-xl'>
                        Tell us about your business and where you want it to go, and we&apos;ll get back to you with a free quote.
                    </p>

                    <dl className='mt-14 grid max-w-md grid-cols-2 gap-x-8 gap-y-8 border-t border-ink/15 pt-8'>
                        <div>
                            <dt className='label text-stone'>Studio</dt>
                            <dd className='mt-2 text-base font-medium'>{siteConfig.legalName}</dd>
                        </div>

                        <div>
                            <dt className='label text-stone'>Based in</dt>
                            <dd className='mt-2 text-base font-medium'>{siteConfig.location}</dd>
                        </div>

                        <div>
                            <dt className='label text-stone'>Local time</dt>
                            <dd className='mt-2 text-base font-medium'><LocalTime /></dd>
                        </div>

                        <div>
                            <dt className='label text-stone'>In business</dt>
                            <dd className='mt-2 text-base font-medium'>12.5 years</dd>
                        </div>
                    </dl>
                </div>

                <div className='lg:col-span-6 lg:col-start-7'>
                    {status === 'success' ? (
                        <div
                            ref={successRef}
                            role='status'
                            tabIndex={-1}
                            className='flex min-h-[28rem] flex-col justify-center border-t-2 border-ink pt-10 outline-none'
                        >
                            <HouseMark className='size-16' />

                            <p className='display mt-10 text-[clamp(2.4rem,4.4vw,4.4rem)] leading-[0.92] wdth-87'>
                                Thank you{sentTo ? `, ${sentTo}` : ''}.
                            </p>

                            <p className='mt-6 max-w-[38ch] text-lg leading-relaxed text-stone'>
                                Your request is in. We&apos;ll get back to you by email soon.
                            </p>

                            <button
                                type='button'
                                onClick={reset}
                                className='mt-10 w-fit border-b border-ink pb-1 text-base font-semibold transition-colors hover:border-signal-deep hover:text-signal-deep'
                            >
                                Send another request
                            </button>
                        </div>
                    ) : (
                        <form ref={formRef} noValidate onSubmit={submit} className='relative flex flex-col gap-10'>
                            <div data-field='' className='grid gap-10 sm:grid-cols-2'>
                                <TextField
                                    id='quote-first-name'
                                    name='firstName'
                                    label="First name"
                                    value={fields.firstName}
                                    error={errors.firstName}
                                    autoComplete='given-name'
                                    onChange={update}
                                />

                                <TextField
                                    id='quote-last-name'
                                    name='lastName'
                                    label="Last name"
                                    value={fields.lastName}
                                    error={errors.lastName}
                                    autoComplete='family-name'
                                    onChange={update}
                                />
                            </div>

                            <div data-field=''>
                                <TextField
                                    id='quote-email'
                                    name='email'
                                    type='email'
                                    label="Email"
                                    value={fields.email}
                                    error={errors.email}
                                    autoComplete='email'
                                    onChange={update}
                                />
                            </div>

                            <fieldset data-field=''>
                                <legend className='label text-stone'>What can we help with?</legend>

                                <div className='mt-4 flex flex-wrap gap-2.5'>
                                    {(Object.keys(SERVICE_LABELS) as Array<ServiceKey>).map(service => {
                                        const checked = services.includes(service)

                                        return (
                                            <label
                                                key={service}
                                                className={cn(
                                                    'group relative inline-flex h-12 cursor-pointer select-none items-center gap-2.5 border px-4 text-sm font-semibold transition-colors duration-300',
                                                    'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-signal',
                                                    checked ? 'border-ink bg-ink text-paper' : 'border-ink/25 text-ink hover:border-ink'
                                                )}
                                            >
                                                <input
                                                    type='checkbox'
                                                    name='services'
                                                    value={service}
                                                    checked={checked}
                                                    onChange={() => toggleService(service)}
                                                    className='sr-only'
                                                />

                                                <span
                                                    aria-hidden='true'
                                                    className={cn(
                                                        'grid size-4 place-items-center border transition-colors',
                                                        checked ? 'border-signal bg-signal' : 'border-current'
                                                    )}
                                                >
                                                    {checked && (
                                                        <svg viewBox='0 0 12 12' className='size-3 text-ink'>
                                                            <path d='M2.5 6.5 5 9l4.5-5.5' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
                                                        </svg>
                                                    )}
                                                </span>

                                                {SERVICE_LABELS[service]}

                                                {SERVICE_NOTES[service] && (
                                                    <span className={cn('label text-[0.6rem]', checked ? 'text-frost' : 'text-stone')}>
                                                        {SERVICE_NOTES[service]}
                                                    </span>
                                                )}
                                            </label>
                                        )
                                    })}
                                </div>
                            </fieldset>

                            <div data-field='' className='flex flex-col'>
                                <label htmlFor='quote-message' className='label text-stone'>
                                    About your business and project
                                    <span aria-hidden='true' className='text-signal-deep'> *</span>
                                </label>

                                <textarea
                                    id='quote-message'
                                    name='message'
                                    rows={5}
                                    required
                                    value={fields.message}
                                    maxLength={QUOTE_LIMITS.message}
                                    aria-invalid={errors.message ? true : undefined}
                                    aria-describedby={errors.message ? 'quote-message-error' : 'quote-message-hint'}
                                    onChange={update}
                                    className={cn(
                                        'mt-3 w-full resize-y border-2 bg-mist/60 p-4 text-lg leading-relaxed text-ink outline-none transition-colors duration-300 focus:border-signal',
                                        errors.message ? 'border-destructive' : 'border-transparent hover:border-ink/20'
                                    )}
                                />

                                {errors.message ? (
                                    <p id='quote-message-error' className='mt-2 text-sm font-medium text-destructive'>
                                        {errors.message}
                                    </p>
                                ) : (
                                    <p id='quote-message-hint' className='mt-2 text-sm text-stone'>
                                        What you do, who your customers are, and what you want your website to achieve.
                                    </p>
                                )}
                            </div>

                            <div aria-hidden='true' className='absolute -left-[9999px] top-auto size-px overflow-hidden'>
                                <label htmlFor='quote-website'>Leave this field empty</label>
                                <input
                                    id='quote-website'
                                    name='website'
                                    type='text'
                                    tabIndex={-1}
                                    autoComplete='off'
                                    value={fields.website}
                                    onChange={update}
                                />
                            </div>

                            <div data-field='' className='flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between'>
                                <button
                                    type='submit'
                                    disabled={submitting}
                                    className='group inline-flex h-16 items-center justify-center gap-3 bg-ink px-9 text-base font-semibold text-paper transition-colors duration-300 hover:bg-signal hover:text-ink disabled:cursor-wait disabled:opacity-70'
                                >
                                    {submitting ? (
                                        <>
                                            <LoaderCircle aria-hidden='true' className='size-5 animate-spin' />
                                            Sending your request
                                        </>
                                    ) : (
                                        <>
                                            Send my request
                                            <ArrowRight aria-hidden='true' className='size-5 transition-transform duration-300 group-hover:translate-x-1' />
                                        </>
                                    )}
                                </button>

                                <p className='label text-stone'>* Required</p>
                            </div>

                            <p role='alert' className={cn('text-base font-medium text-destructive', !notice && 'sr-only')}>
                                {notice}
                            </p>
                        </form>
                    )}
                </div>
            </div>
        </section>
    )
}
